import { useEffect, useRef, useState } from 'react';
import { debounce } from 'underscore';
import classNames from 'classnames';

import { IInputWithOverlayProps } from './input-with-overlay.types';
import OptionalComponent from '@ui/optional-component/optional-component';

import './input-with-overlay.css';
import Button from '@ui/buttons/button/button';
// this input wrapper is used to allow a button or input have an overlay on top of it
// Why? because under some situations we want to be able to add anything in a button or input, including more buttons/inputs
// and you can't have a button inside a button or an anchor inside an anchor, etc., This allows us to overlay through css
// without needing to nest elements
const InputWithOverlay = ({
  label = '',
  jsxLabel,
  asButton = false,
  buttonProps = {},
  inputProps,
  renderInnerContent,
  children,
  wrapperClass,
  onDebounce,
  debounceTime = 100,
  enableChildrenPointerEvents = true,
  innerContentClassName = '',
  innerContentStyle = {},
  renderButtonContent,
  error,
  dirtyError,
  inputRef,
  inputHeight,
  inputWidth,
  containerRef,
  border,
  innerContentRef,
  unhoveredElipses,
  background,
  hoverBackground,
  hoverBorder,
  'data-component': dataComponent,
  isBorderless = false,
}: IInputWithOverlayProps) => {
  const { style: inputStyle, ...restInputProps } = inputProps || {};

  const [dirty, setDirty] = useState(false);
  const [hasError, setHasError] = useState(error || false);

  const handleDebounce = useRef(
    debounce((value: string) => {
      onDebounce?.(value);
    }, debounceTime)
  ).current;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    inputProps?.onChange?.(e);
    handleDebounce(value);
  };

  useEffect(() => {
    setHasError(!!error);
  }, [error]);

  useEffect(() => {
    if (!dirtyError) return;
    dirtyError({ dirty, setHasError: setHasError });
  }, [dirty, dirtyError, setHasError]);

  return (
    <div
      data-component={dataComponent || 'InputWithOverlay'}
      className={classNames('input-with-overlay-wrapper', wrapperClass)}
      ref={containerRef}
    >
      <OptionalComponent display={!!label && !jsxLabel}>
        <label className="L2">{label}</label>
      </OptionalComponent>

      <OptionalComponent display={!!jsxLabel}>
        <>{jsxLabel?.()}</>
      </OptionalComponent>

      <div className="input-with-overlay-input-wrapper">
        <OptionalComponent display={!asButton}>
          <input
            {...restInputProps}
            style={{
              ...inputStyle,
              ...(inputHeight ? { '--input-with-overlay-input-height': inputHeight } : {}),
              ...(inputWidth ? { '--input-with-overlay-input-width': inputWidth } : {}),
              ...(border && !isBorderless ? { '--input-with-overlay-border': border } : {}),
              ...(background ? { '--input-with-overlay-background': background } : {}),
              ...(hoverBackground ? { '--input-with-overlay-hover-bg-color': hoverBackground } : {}),
              ...(hoverBorder && !isBorderless ? { '--input-with-overlay-hover-border': hoverBorder } : {}),
            }}
            ref={inputRef}
            className={classNames(
              'input-with-overlay',
              { error: hasError, ['unhovered-ellipses']: unhoveredElipses },
              inputProps?.className
            )}
            onChange={handleChange}
            onBlur={(e) => {
              setDirty(true);
              inputProps?.onBlur?.(e);
            }}
          />
        </OptionalComponent>

        <OptionalComponent display={asButton}>
          <Button {...buttonProps} clear className={classNames('button-with-overlay', buttonProps?.className || '')}>
            {renderButtonContent?.()}
          </Button>
        </OptionalComponent>
        <div
          className={classNames('input-with-overlay-children', innerContentClassName, {
            ['enable-children-pointer-events']: enableChildrenPointerEvents,
          })}
          style={innerContentStyle}
          ref={innerContentRef}
        >
          {renderInnerContent?.()}
        </div>
      </div>
      {children}
    </div>
  );
};

export default InputWithOverlay;
